import { SegmentationOptionsFactory } from '@salecycle/experimental/Modal/SegmentationOptionsFactory';
import { FilterOption, SegmentationOption } from './types';
import {
  OPERATORS_TRANSLATION_PREFIX_DEFAULT,
  VALUES_TRANSLATION_PREFIX_DEFAULT,
  segmentationBasketOptions,
  segmentationBrowseOptions,
  segmentationPurchaseOptions,
} from './constants';
import { ISelectOption, ISelectOptionWithChildren } from '@salecycle/ui-component-library';
import { TFunction } from 'react-i18next';

export class FilterOptionsFactory implements SegmentationOptionsFactory {
  public constructor(private t: TFunction, isBrowseAband?: boolean, removeSmsOptions?: boolean, isPurchase?: boolean) {
    const selectedOptions = isPurchase
      ? segmentationPurchaseOptions
      : isBrowseAband
      ? segmentationBrowseOptions
      : segmentationBasketOptions;
    const options = Object.assign({}, selectedOptions);

    if (removeSmsOptions) {
      delete (options as any).demographics['phone.mobile'];
    }

    this.ruleTypeOptions = this.getTypes(options);
    this.optionsList = this.listAllFilterOptions(selectedOptions);
  }

  public ruleTypeOptions: ISelectOptionWithChildren[];
  private optionsList: { [key: string]: FilterOption };

  public defaultValuesSelector(selectedRule: string) {
    const ruleOption = this.optionsList[selectedRule];
    return {
      operator: {
        value: ruleOption.defaultOperator,
        label: this.t(
          `${OPERATORS_TRANSLATION_PREFIX_DEFAULT}.${this.sanitiseJSONStringKey(ruleOption.defaultOperator)}`,
        ),
      },
      value: {
        value: ruleOption.defaultValue,
        label: this.t(`${VALUES_TRANSLATION_PREFIX_DEFAULT}.${this.sanitiseJSONStringKey(ruleOption.defaultValue)}`),
      },
    };
  }

  public operatorsSelector(selectedRule: string) {
    const ruleOption = this.optionsList[selectedRule];
    if (!ruleOption) {
      return null;
    }

    const opsTPath: string = ruleOption?.operatorsTranslationPrefix ?? OPERATORS_TRANSLATION_PREFIX_DEFAULT;
    const operators: ISelectOption<string>[] = ruleOption.operators?.map(s => ({
      value: s,
      label: this.t(`${opsTPath}.${s}`),
    }));
    return operators;
  }

  public valuesSelector(selectedRule: string) {
    const ruleOption = this.optionsList[selectedRule];
    const valuesTPath: string = ruleOption.valuesTranslationPrefix ?? VALUES_TRANSLATION_PREFIX_DEFAULT;
    const values: ISelectOption<string>[] = ruleOption.values.map((s: string) => ({
      value: s,
      label: this.t(`${valuesTPath}.${s}`),
    }));
    return values;
  }

  public ruleOptionCreator(selected: string) {
    if (!selected) {
      return;
    }
    return {
      value: selected,
      label: this.t(`segmentationRules.types.${this.sanitiseJSONStringKey(selected)}`),
      children: undefined,
    };
  }

  public operatorOptionCreator(selected: string, selectedRule?: string) {
    if (!selected) {
      return;
    }
    const opsTPath =
      selectedRule && this.optionsList[selectedRule]?.operatorsTranslationPrefix
        ? this.optionsList[selectedRule].operatorsTranslationPrefix
        : OPERATORS_TRANSLATION_PREFIX_DEFAULT;
    return {
      value: selected,
      label: this.t(`${opsTPath}.${this.sanitiseJSONStringKey(selected)}`),
    };
  }

  public valueOptionCreator(rule: string, value: string) {
    if (!rule) {
      return;
    }

    const tPath = this.optionsList[rule].valuesTranslationPrefix ?? VALUES_TRANSLATION_PREFIX_DEFAULT;

    return {
      value: rule,
      label: this.t(`${tPath}.${this.sanitiseJSONStringKey(value)}`),
    };
  }

  public inputTypeSelector(selectedRule: string) {
    return this.optionsList[selectedRule]?.filterType;
  }

  public getOptionsList() {
    return this.optionsList;
  }

  private sanitiseJSONStringKey = (s: string): string => {
    return s.replaceAll('.', '_');
  };

  private instanceOfFilterOption = (object: object): object is FilterOption => {
    return 'operators' in object && 'values' in object && 'defaultOperator' in object && 'defaultValue' in object;
  };

  private instanceOfSegmentationOption = (object: object): object is SegmentationOption => {
    const keys = Object.keys(object);
    let isType = true;
    keys.forEach(key => {
      isType = isType && (this.instanceOfFilterOption(object[key]) || this.instanceOfSegmentationOption(object[key]));
    });
    return isType;
  };

  private getTypes = (options: object): ISelectOptionWithChildren[] => {
    const types: ISelectOptionWithChildren[] = Object.keys(options).map(s => ({
      value: s,
      label: this.t(
        `segmentationRules.${
          this.instanceOfFilterOption(options[s]) ? 'types' : 'section'
        }.${this.sanitiseJSONStringKey(s)}`, // replacing . with _ to access json file
      ),
      children: this.instanceOfFilterOption(options[s]) ? [] : this.getTypes(options[s]),
    }));
    return types;
  };

  /** This flattens the segmentationOptions-like multi level object tree into a 1 level deep string keyed object */
  private listAllFilterOptions = (segmentationOptionsInput: object): { [key: string]: FilterOption } => {
    const filterOps = {};
    const recursiveFind = (options: object) => {
      Object.keys(options).forEach(key => {
        if (this.instanceOfFilterOption(options[key])) {
          filterOps[key] = options[key];
          return;
        }
        recursiveFind(options[key]);
      });
    };
    recursiveFind(segmentationOptionsInput);
    return filterOps;
  };
}
